import React from 'react';
import { InputBase, styled, inputBaseClasses } from '@mui/material';
import { isNil, debounce } from 'lodash';
import classnames from 'classnames';
import EditTextIcon from '../../icons/EditTextIcon';
import './TextEditable';

const muiStyles = {
  darkHover: {
    height: '100%',
    fontSize: 16,
    fontWeight: 400,
    width: '100%',
    padding: 2,
    display: 'flex',
    alignItems: 'center',
    border: '1px solid transparent',
    '&:hover': {
      border: '1px solid white',
    },
    [`.${inputBaseClasses.input}`]: {
      fontSize: 16,
      fontWeight: 300,
      color: '#fff',
      padding: 2,
      letterSpacing: 0.5,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    [`&.${inputBaseClasses.multiline}`]: {
      [`& .${inputBaseClasses.input}`]: {
        whiteSpace: 'pre-wrap',
        overflow: 'auto',
        textOverflow: 'clip',
      },
    },
  },
  lightHover: {
    height: '100%',
    fontSize: 16,
    fontWeight: 400,
    width: '100%',
    padding: 2,
    display: 'flex',
    alignItems: 'center',
    border: '1px solid transparent',
    '&:hover': {
      border: '1px solid #DFE8F0',
      borderRadius: 2,
    },
    [`.${inputBaseClasses.input}`]: {
      fontSize: 16,
      fontWeight: 400,
      color: '#1F2F40',
      letterSpacing: 0.5,
      padding: 2,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    [`&.${inputBaseClasses.multiline}`]: {
      [`& .${inputBaseClasses.input}`]: {
        whiteSpace: 'pre-wrap',
        overflow: 'auto',
        textOverflow: 'clip',
      },
    },
  },
  dark: {
    height: '100%',
    fontSize: 16,
    fontWeight: 400,
    width: '100%',
    padding: 2,
    display: 'flex',
    alignItems: 'center',
    [`.${inputBaseClasses.input}`]: {
      fontSize: 16,
      fontWeight: 300,
      color: '#fff',
      padding: 2,
      letterSpacing: 0.5,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    [`&.${inputBaseClasses.multiline}`]: {
      [`& .${inputBaseClasses.input}`]: {
        whiteSpace: 'pre-wrap',
        overflow: 'auto',
        textOverflow: 'clip',
      },
    },
  },
  light: {
    height: '100%',
    fontSize: 16,
    fontWeight: 400,
    width: '100%',
    padding: 2,
    display: 'flex',
    alignItems: 'center',
    [`.${inputBaseClasses.input}`]: {
      fontSize: 16,
      fontWeight: 400,
      color: '#1F2F40',
      letterSpacing: 0.5,
      padding: 2,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    [`&.${inputBaseClasses.multiline}`]: {
      [`& .${inputBaseClasses.input}`]: {
        whiteSpace: 'pre-wrap',
        overflow: 'auto',
        textOverflow: 'clip',
      },
    },
  },
  default: {
    height: '100%',
    fontSize: 16,
    fontWeight: 400,
    width: '100%',
    padding: '6px 10px',
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #DFE8F0',
    borderRadius: 8,
    [`.${inputBaseClasses.input}`]: {
      fontSize: 16,
      fontWeight: 400,
      color: '#1F2F40',
      letterSpacing: 0.5,
      padding: 2,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '&.Mui-error': {
      border: '1px solid #b53c20',
    },
    [`&.${inputBaseClasses.multiline}`]: {
      [`& .${inputBaseClasses.input}`]: {
        whiteSpace: 'pre-wrap',
        overflow: 'auto',
        textOverflow: 'clip',
      },
    },
  },
};

const StyledInputDarkHover = styled(InputBase)(muiStyles.darkHover);
const StyledInputLightHover = styled(InputBase)(muiStyles.lightHover);
const StyledInputDark = styled(InputBase)(muiStyles.dark);
const StyledInputLight = styled(InputBase)(muiStyles.light);
const StyledInputDefault = styled(InputBase)(muiStyles.default);
class DebouncedText extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      currentText: props.value,
    };

    this._onChange = this._onChange.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onBlur = this._onBlur.bind(this);
    this._commitChange = this._commitChange.bind(this);

    if (props.debounceTimeMs > 0) {
      this._commitChange = debounce(this._commitChange, props.debounceTimeMs);
      this._flushChange = this._commitChange.flush;
      this._cancelChange = this._commitChange.cancel;
    } else {
      this._flushChange = this._commitChange;
      this._cancelChange = null;
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.id !== this.props.id || prevProps.value !== this.props.value) {
      this.setState({
        currentText: this.props.value,
      });
    }
  }

  componentWillUnmount() {
    if (this._cancelChange) {
      this._cancelChange();
    }
  }

  _onChange(event) {
    const text = event.target.value;
    this.setState(
      {
        currentText: text,
      },
      () => {
        if (this.props.debounceTimeMs > 0) {
          this._commitChange();
        }
      },
    );
  }

  _onKeyDown(event) {
    if (event.keyCode === 13) {
      this._flushChange();
    }

    if (this.props.onKeyDown) {
      this.props.onKeyDown(event);
    }
  }

  _onBlur(event) {
    this._flushChange();

    if (this.props.onBlur) {
      this.props.onBlur(event);
    }
  }

  _commitChange() {
    if (this.props.onChange) {
      this.props.onChange(this.state.currentText);
    }
  }

  render() {
    const {
      debounceTimeMs,
      className,
      rootStyle,
      inputTheme,
      displayIcon,
      value,
      onChange,
      onKeyDown,
      onBlur,
      onTextFieldRef,
      multiline,
      rows,
      ['data-testid']: dataTestId,
      ...otherProps
    } = this.props;
    const { currentText } = this.state;
    const divClassName = classnames('textInput', className);
    const currentValue = !isNil(currentText) ? currentText : '';

    const baseProps = {
      margin: 'dense',
      type: 'text',
      inputProps: {
        autoComplete: 'new-password',
        'data-testid': dataTestId,
      },
      value: currentValue,
      ref: onTextFieldRef,
      onChange: this._onChange,
      multiline: multiline,
      // Only set rows when explicitly provided — omitting it lets minRows/maxRows
      // (passed via otherProps) control auto-grow behavior in multiline mode.
      ...(rows !== undefined && rows !== null ? { rows } : {}),
      onKeyDown: this._onKeyDown,
      onBlur: this._onBlur,
      ...otherProps,
    };

    let inputField = null;
    switch (inputTheme) {
      case 'darkHover':
        inputField = <StyledInputDarkHover {...baseProps} />;
        break;

      case 'lightHover':
        inputField = <StyledInputLightHover {...baseProps} />;
        break;

      case 'dark':
        inputField = <StyledInputDark {...baseProps} />;
        break;

      case 'light':
        inputField = <StyledInputLight {...baseProps} />;
        break;

      case 'default':
      default:
        inputField = <StyledInputDefault {...baseProps} />;
    }

    return (
      <div className={divClassName} style={rootStyle}>
        {this.props.disabled === false && displayIcon === true ? <EditTextIcon /> : null}
        {inputField}
      </div>
    );
  }
}

DebouncedText.defaultProps = {
  fullWidth: true,
  disabled: false,
  displayIcon: false,
  inputTheme: 'default',
  rootStyle: {},
  debounceTimeMs: 0,
  onTextFieldRef: () => {},
  onBlur: () => {},
  onKeyDown: () => {},
  multiline: false,
};

export default DebouncedText;
